/* dc-core.js — interacciones del tema Dulce Comienzo (vanilla, defer) */
(() => {
  'use strict';

  // 1) Scroll reveal
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;
  const observeReveals = (root = document) => {
    if (!io) return;
    root.querySelectorAll('[data-reveal],[data-reveal-x]').forEach((el) => io.observe(el));
  };

  // 2) Acordeón (uno abierto a la vez dentro de <dc-accordion>)
  class DCAccordion extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const head = e.target.closest('[data-acc-head]');
        if (!head) return;
        const item = head.closest('[data-acc-item]');
        const open = item.classList.contains('is-open');
        this.querySelectorAll('[data-acc-item]').forEach((i) => i.classList.remove('is-open'));
        if (!open) item.classList.add('is-open');
      });
    }
  }

  // 3) Tabs (<dc-tabs>): [data-tab] botones, [data-panel] paneles
  class DCTabs extends HTMLElement {
    connectedCallback() {
      const tabs = [...this.querySelectorAll('[data-tab]')];
      const panels = [...this.querySelectorAll('[data-panel]')];
      const activate = (key) => {
        tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.tab === key));
        panels.forEach((p) => { p.hidden = p.dataset.panel !== key; });
        if (this.dataset.accentVar) {
          const btn = tabs.find((t) => t.dataset.tab === key);
          if (btn && btn.dataset.accent) this.style.setProperty('--dc-accent', btn.dataset.accent);
        }
      };
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-tab]'); if (t) activate(t.dataset.tab);
      });
      if (tabs[0]) activate(tabs[0].dataset.tab);
    }
  }

  // 4) CTA con texto rotativo (<dc-rotator> con [data-phrase] hijos)
  class DCRotator extends HTMLElement {
    connectedCallback() {
      const phrases = [...this.querySelectorAll('[data-phrase]')];
      if (phrases.length < 2) return;
      let i = 0;
      const every = parseInt(this.dataset.interval || '5000', 10);
      setInterval(() => {
        // data-paused: alguien más se adueñó del texto del botón (p. ej. dc-landing.js
        // tras elegir un color). No rotamos ni tocamos estilos hasta que lo suelte.
        if (this.hasAttribute('data-paused')) return;
        phrases[i].style.opacity = '0';
        const next = (i + 1) % phrases.length;
        setTimeout(() => {
          phrases[i].hidden = true;
          phrases[next].hidden = false;
          phrases[next].style.opacity = '1';
          i = next;
        }, 420);
      }, every);
    }
  }

  // 5) Countdown (<dc-countdown>)
  //    data-deadline="ISO"                    → fecha fija.
  //    data-duration="432000" [data-anchor]   → ciclo que se reinicia solo.
  //    sin atributos                          → medianoche local, salvo que otro
  //                                             countdown de la página tenga ciclo.

  // 'AAAA-MM-DD' → medianoche local de ese día. Vacío o ilegible → 2000-01-01.
  // Se parsea a mano porque new Date('2026-08-24') lo interpreta como UTC y el ciclo
  // saltaría a una hora distinta según el huso de cada clienta.
  // La fecha de reserva es solo un origen: lo único que importa es que sea la misma
  // en todos los navegadores para que todas vean el mismo número.
  const anchorTime = (raw) => {
    const m = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(raw || '');
    if (!m) return new Date(2000, 0, 1).getTime();
    return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
  };

  class DCCountdown extends HTMLElement {
    connectedCallback() {
      const out = this.querySelector('[data-cd-out]') || this;
      // Sin ciclo propio, hereda el del primer countdown de la página que tenga uno:
      // así el cierre de la landing no promete "termina en 5h" mientras el hero dice
      // "4d". Se busca en el documento y no en una variable compartida porque el orden
      // en que se conectan los custom elements no está garantizado.
      const source = this.dataset.duration ? this : document.querySelector('dc-countdown[data-duration]');
      const cycle = source ? parseInt(source.dataset.duration, 10) * 1000 : 0;
      const anchor = source ? anchorTime(source.dataset.anchor) : 0;

      const target = () => {
        if (this.dataset.deadline) return new Date(this.dataset.deadline);
        if (cycle > 0) {
          // Siguiente múltiplo del ciclo contado desde el ancla. Al cruzar cero, este
          // mismo cálculo devuelve el ciclo siguiente: el reloj vuelve a empezar solo,
          // sin que nadie reconfigure nada.
          const done = Math.floor((Date.now() - anchor) / cycle) + 1;
          return new Date(anchor + done * cycle);
        }
        const d = new Date(); d.setHours(24, 0, 0, 0); return d;
      };
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        let diff = Math.max(0, target() - Date.now());
        const dd = Math.floor(diff / 8.64e7); diff -= dd * 8.64e7;
        const h = Math.floor(diff / 3.6e6); diff -= h * 3.6e6;
        const m = Math.floor(diff / 6e4); diff -= m * 6e4;
        const s = Math.floor(diff / 1e3);
        // Los días solo se escriben si los hay: el último día del ciclo se ve
        // exactamente igual que el countdown de siempre.
        out.textContent = (dd > 0 ? dd + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
      };
      tick(); setInterval(tick, 1000);
    }
  }

  // 6) Sticky bar (<dc-sticky-bar data-after="700">)
  class DCStickyBar extends HTMLElement {
    connectedCallback() {
      const after = parseInt(this.dataset.after || '700', 10);
      const root = document.documentElement;
      let visible = null; // null = todavía sin calcular
      let ticking = false;

      const apply = () => {
        ticking = false;
        const next = window.scrollY > after;
        if (next === visible) return; // nada que hacer: ni leemos ni escribimos estilos
        visible = next;
        this.classList.toggle('is-visible', next);
        // Empuja el WhatsApp flotante por encima de la barra cuando está visible.
        // Solo se escribe al entrar/salir: hacerlo en cada evento de scroll obligaba
        // al navegador a recalcular el estilo de TODA la página varias veces por
        // segundo y era lo que hacía que los toques respondieran tarde (INP).
        root.style.setProperty('--dc-sticky-offset', next ? this.offsetHeight + 'px' : '0px');
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(apply);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      apply();
    }
  }

  // 7) Carrusel: mejora accesibilidad; el scroll-snap es CSS. (<dc-carousel>)
  class DCCarousel extends HTMLElement {
    connectedCallback() { this.setAttribute('role', 'region'); this.tabIndex = 0; }
  }

  // 8) Autoplay de videos solo cuando están visibles (reel, ahorra datos/CPU)

  // Le da la URL al video la primera vez que se acerca a pantalla.
  // El marcado sale con <source data-src> y sin src, así que durante la carga de la
  // página no se descarga nada de video (competía con la imagen del hero, el LCP).
  // load() reinicia la selección de recurso y, con ella, el flag de autoplay: el
  // navegador arranca el video por su cuenta. Ojo: NO sirve llamar solo a play(),
  // porque play() desde JS sí pasa por la política de autoplay y el móvil lo bloquea.
  function primeVideo(v) {
    if (v.dataset.dcPrimed) return;
    v.dataset.dcPrimed = '1';
    // El poster primero: es lo mas ligero y es lo que tapa el hueco antes que nada.
    // Viaja en data-poster porque el navegador no difiere el poster de un <video>
    // ni con preload="none": se descargaba durante el arranque de la pagina y le
    // robaba ancho de banda a la foto del hero (el LCP).
    if (v.dataset.poster && !v.getAttribute('poster')) v.poster = v.dataset.poster;
    const source = v.querySelector('source[data-src]');
    if (!source) return; // ya trae src propio (p. ej. video_tag de Shopify): nada que hacer
    source.src = source.dataset.src;
    v.preload = 'auto';
    v.load();
  }

  let videoIO;
  function observeVideos(root) {
    const scope = root || document;
    const vids = scope.querySelectorAll('video.dc-strip__img, video.dc-vid__media');
    if (!vids.length) return;
    if (!('IntersectionObserver' in window)) {
      vids.forEach((v) => { primeVideo(v); const p = v.play(); if (p) p.catch(() => {}); });
      return;
    }
    if (!videoIO) {
      videoIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const v = e.target;
          if (e.isIntersecting) {
            primeVideo(v);
            // Red de seguridad por si el navegador no dispara el autoplay solo.
            const p = v.play(); if (p) p.catch(() => {});
          } else if (v.dataset.dcPrimed) {
            v.pause();
          }
        });
        // Margen de anticipación: da tiempo a que el video cargue antes de que el
        // usuario llegue, sin descargarlo durante el arranque de la página.
      }, { threshold: 0.01, rootMargin: '200px 0px 500px 0px' });
    }
    vids.forEach((v) => videoIO.observe(v));
  }

  // 9) Cargadores de media: mientras la foto o el video no han llegado, el hueco
  //    muestra el esqueleto de marca (dc-base.css) en vez de un rectángulo vacío.
  //
  //    Solo se vigila lo que está cerca de pantalla. Los <img loading="lazy"> del
  //    final de la landing ni siquiera han empezado a pedirse, así que arrancarles
  //    un temporizador de seguridad al cargar la página apagaría el cargador antes
  //    de que hiciera falta.

  const ldReady = (box) => { box.classList.add('is-ready'); };

  // Engancha el cargador de una caja a su medio. Idempotente por diseño: se vuelve
  // a llamar en cada cambio de color del hero, cuando la imagen se reemplaza.
  function bindLoader(box) {
    const media = box.querySelector('img, video, iframe');
    if (!media) { ldReady(box); return; }

    let done = false;
    const ready = () => { if (done) return; done = true; ldReady(box); };

    if (media.tagName === 'IMG') {
      // Ya decodificada (cache o HTML pintado antes de que corriera el JS).
      if (media.complete && media.naturalWidth > 0) { ready(); return; }
      media.addEventListener('load', ready, { once: true });
      media.addEventListener('error', ready, { once: true });
    } else if (media.tagName === 'VIDEO') {
      if (media.readyState >= 2) { ready(); return; }
      ['loadeddata', 'canplay', 'playing', 'error'].forEach((ev) => media.addEventListener(ev, ready, { once: true }));
      // Con póster el hueco ya está tapado por una imagen: en cuanto esa se pinta,
      // el cargador sobra aunque el video siga descargando.
      if (media.poster) { const p = new Image(); p.onload = ready; p.onerror = ready; p.src = media.poster; }
    } else {
      media.addEventListener('load', ready, { once: true });
      media.addEventListener('error', ready, { once: true });
    }

    // Red de seguridad: un cargador que se queda girando para siempre se lee como
    // página rota. A los 12 s se apaga y se deja ver lo que haya debajo.
    setTimeout(ready, 12000);
  }

  let loaderIO;
  function observeLoaders(root) {
    const scope = root || document;
    const boxes = scope.querySelectorAll('[data-dc-ld]');
    if (!boxes.length) return;
    if (!('IntersectionObserver' in window)) { boxes.forEach(bindLoader); return; }
    if (!loaderIO) {
      loaderIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          loaderIO.unobserve(e.target);
          bindLoader(e.target);
        });
      }, { rootMargin: '300px 0px 600px 0px' });
    }
    boxes.forEach((b) => loaderIO.observe(b));
  }

  // Esqueletos de fondo (.dc-ld-bg): la imagen lleva el degradado en su propio
  // background. Aqui solo se apaga al llegar la foto, porque animar
  // background-position repinta y dejarlo corriendo detras de decenas de fotos ya
  // visibles calienta el telefono para nada.
  function observeSkeletons(root) {
    (root || document).querySelectorAll('img.dc-ld-bg').forEach((img) => {
      if (img.__dcSkel) return;
      img.__dcSkel = true;
      const off = () => img.classList.add('is-ready');
      if (img.complete && img.naturalWidth > 0) { off(); return; }
      img.addEventListener('load', off, { once: true });
      img.addEventListener('error', off, { once: true });
    });
  }

  // Reinicia el cargador de un medio que se acaba de reemplazar (cambio de color
  // del hero). Lo usa dc-landing.js.
  function restartLoader(el) {
    const box = el && el.closest ? el.closest('[data-dc-ld]') : null;
    if (!box) return;
    box.classList.remove('is-ready');
    bindLoader(box);
  }

  // 9) Contador "viendo ahora" con cambios realistas (poco frecuentes, ±1/±2, acotado)
  function animateViewers(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-viewers]').forEach((el) => {
      // Solo el contador de texto (nunca un contenedor con hijos → evita borrar tarjetas)
      if (el.children.length || el.__dcViewers) return;
      el.__dcViewers = true;
      let val = parseInt(el.textContent, 10);
      if (isNaN(val)) val = 14;
      const min = parseInt(el.dataset.viewersMin, 10) || Math.max(2, val - 5);
      const max = parseInt(el.dataset.viewersMax, 10) || (val + 12);
      const step = () => {
        // No cambia siempre: se siente real (a veces se queda igual)
        if (Math.random() < 0.65) {
          let dir = Math.random() < 0.5 ? -1 : 1;
          if (val <= min) dir = 1; else if (val >= max) dir = -1;
          const mag = Math.random() < 0.82 ? 1 : 2; // casi siempre ±1, a veces ±2
          const next = Math.max(min, Math.min(max, val + dir * mag));
          if (next !== val) {
            val = next;
            el.textContent = val;
            el.style.transition = 'opacity .3s ease';
            el.style.opacity = '.4';
            setTimeout(() => { el.style.opacity = '1'; }, 220);
          }
        }
        setTimeout(step, 9000 + Math.random() * 11000); // cada 9–20 s (no muy seguido)
      };
      setTimeout(step, 6000 + Math.random() * 6000); // primer cambio a los 6–12 s
    });
  }

  customElements.define('dc-accordion', DCAccordion);
  customElements.define('dc-tabs', DCTabs);
  customElements.define('dc-rotator', DCRotator);
  customElements.define('dc-countdown', DCCountdown);
  customElements.define('dc-sticky-bar', DCStickyBar);
  customElements.define('dc-carousel', DCCarousel);

  document.addEventListener('DOMContentLoaded', () => { observeReveals(); observeVideos(); observeLoaders(); observeSkeletons(); animateViewers(); });
  document.addEventListener('shopify:section:load', (e) => { observeReveals(e.target); observeVideos(e.target); observeLoaders(e.target); observeSkeletons(e.target); animateViewers(e.target); }); // Theme Editor
  window.DCCore = { observeReveals, observeVideos, observeLoaders, observeSkeletons, restartLoader, animateViewers };
})();
